import { ApolloError } from '@apollo/client';

import { getFieldErrors } from '@utils/graphqlError';

import type { FormValues } from './TaskFormStack';

// TA-10140: maps backend field_errors keys (snake_case) onto this form's
// Formik field names (camelCase). Kept in its own module (rather than
// inline in TaskFormStack.tsx) so it can be unit-tested without pulling in
// that screen's heavy transitive imports (navigation stacks, native
// modules, etc.) — see taskFormSubmitHandler.ts for the same pattern.
const TASK_FORM_FIELD_NAME_MAP: Record<string, keyof FormValues> = {
  name: 'name',
  project_id: 'projectId',
  due_date: 'dueDate',
};

export const getTaskFormFieldErrors = (
  error: ApolloError
): Partial<Record<keyof FormValues, string>> => {
  const fieldErrors = getFieldErrors(error);
  const result: Partial<Record<keyof FormValues, string>> = {};
  Object.entries(fieldErrors).forEach(([backendField, messages]) => {
    const formikField = TASK_FORM_FIELD_NAME_MAP[backendField];
    if (formikField && messages?.[0]) {
      result[formikField] = messages[0];
    }
  });
  return result;
};

// PR #5782 code-review fix: the condition TaskFormStack.tsx's generic
// errorMsg banner needs is "were every one of the backend's field_errors
// successfully mapped onto a Formik field?" — true only when there's
// nothing left for the generic banner to cover. Note it is deliberately
// false for a message-only error (no field_errors at all, e.g.
// CONFLICT/INTERNAL_ERROR/network failure), so the banner still shows
// those; an earlier revision suppressed them and left zero feedback.
export const isTaskFormErrorFullyMapped = (error: ApolloError): boolean => {
  const backendFieldCount = Object.keys(getFieldErrors(error)).length;
  const mappedFieldCount = Object.keys(getTaskFormFieldErrors(error)).length;
  return backendFieldCount > 0 && mappedFieldCount === backendFieldCount;
};
